/**
 * localStorage persistence for Arbitrage / Surebet view filters.
 * Separate from API bookmaker prefs (`arbiwin_filter_preferences`)
 * and from Best Odds (`bestOddsFilters`).
 */

import {
  DEFAULT_SUREBET_VIEW_FILTERS,
  TIME_WINDOW_VALUES
} from './surebetViewFilters';

export const SUREBET_VIEW_FILTERS_STORAGE_KEY = 'arbiwin_view_filters';

const STORAGE_VERSION = 1;

/**
 * @param {unknown} value
 * @returns {string[]|null}
 */
const sanitizeStringArrayOrNull = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (!Array.isArray(value)) {
    return null;
  }

  const cleaned = value
    .filter((item) => item !== null && item !== undefined && String(item).trim().length > 0)
    .map((item) => String(item));

  return cleaned;
};

/**
 * Normalize a raw/partial filters object into a safe shape.
 * @param {Object|null|undefined} raw
 * @returns {Object}
 */
export const normalizeSurebetViewFilters = (raw) => {
  const source = raw && typeof raw === 'object' ? raw : {};

  const minMarginRaw = Number(source.minMargin);
  const minMargin = Number.isFinite(minMarginRaw) && minMarginRaw > 0
    ? minMarginRaw
    : 0;

  const searchQuery = typeof source.searchQuery === 'string'
    ? source.searchQuery
    : '';

  const timeWindow = TIME_WINDOW_VALUES.includes(source.timeWindow)
    ? source.timeWindow
    : DEFAULT_SUREBET_VIEW_FILTERS.timeWindow;

  return {
    minMargin,
    searchQuery,
    selectedChampionships: sanitizeStringArrayOrNull(source.selectedChampionships),
    selectedMarketKeys: sanitizeStringArrayOrNull(source.selectedMarketKeys),
    timeWindow
  };
};

/**
 * Load view filters from localStorage. Returns defaults when missing/invalid.
 * @returns {Object}
 */
export const loadSurebetViewFilters = () => {
  try {
    if (typeof localStorage === 'undefined') {
      return { ...DEFAULT_SUREBET_VIEW_FILTERS };
    }

    const raw = localStorage.getItem(SUREBET_VIEW_FILTERS_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_SUREBET_VIEW_FILTERS };
    }

    const parsed = JSON.parse(raw);
    return normalizeSurebetViewFilters(parsed);
  } catch (error) {
    console.warn('Surebet view-filter persistence: failed to load, using defaults', error);
    return { ...DEFAULT_SUREBET_VIEW_FILTERS };
  }
};

/**
 * Save view filters to localStorage.
 * @param {Object} filters
 * @returns {boolean}
 */
export const saveSurebetViewFilters = (filters) => {
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    const normalized = normalizeSurebetViewFilters(filters);
    const payload = {
      version: STORAGE_VERSION,
      ...normalized,
      lastUpdated: new Date().toISOString()
    };

    localStorage.setItem(SUREBET_VIEW_FILTERS_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch (error) {
    console.warn('Surebet view-filter persistence: failed to save', error);
    return false;
  }
};

/**
 * Remove persisted surebet view filters.
 * @returns {boolean}
 */
export const clearSurebetViewFiltersStorage = () => {
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    localStorage.removeItem(SUREBET_VIEW_FILTERS_STORAGE_KEY);
    return true;
  } catch (error) {
    console.warn('Surebet view-filter persistence: failed to clear', error);
    return false;
  }
};

export default {
  SUREBET_VIEW_FILTERS_STORAGE_KEY,
  normalizeSurebetViewFilters,
  loadSurebetViewFilters,
  saveSurebetViewFilters,
  clearSurebetViewFiltersStorage
};
